import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mic, Headphones, Play, Clock, ExternalLink, Radio } from 'lucide-react';

const Podcasts = () => {
  const podcasts = [
    {
      title: 'Our New Normal',
      subtitle: 'Money, Careers and Life',
      description: 'Exploring how money, careers, and life have changed in the modern world. Practical advice for navigating the new normal.',
      image: 'https://images.unsplash.com/photo-1709846486154-f9172678be6f?w=600&q=80',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8ETefv4Tp01xuIX20jSUF5n8',
      episodes: '20+',
      color: 'purple',
    },
    {
      title: 'Everyday Millionaires',
      subtitle: 'Real People. Real Wealth. Real Stories.',
      description: 'Inspiring stories from ordinary Canadians who built extraordinary wealth. Learn their strategies and mindsets.',
      image: 'https://images.unsplash.com/photo-1589903308904-1010c2294adc?w=600&q=80',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8ESzqq6LDvjT87rRnRATOre2',
      episodes: '15+',
      color: 'amber',
    },
    {
      title: 'Deep Dive: Investing in Canada',
      subtitle: 'Breaking down Canadian investing — one concept at a time.',
      description: 'In-depth analysis of Canadian investment concepts. From TFSAs to RRSPs, ETFs to individual stocks.',
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&q=80',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8EQnjuDQn87Xr05OhiZZuI45',
      episodes: '25+',
      color: 'blue',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="podcasts-page">
      <Helmet>
        <title>Canadian Investing Podcasts | Finance in Canada | Our New Normal | Everyday Millionaires | Free Audio Content | Harry Vadalkar</title>
        <meta name="description" content="Listen to free Canadian investing podcasts by Harry Vadalkar. Our New Normal (money, careers, life), Everyday Millionaires (real wealth stories), and Deep Dive: Investing in Canada (TFSA, RRSP, ETFs, stocks). Available on YouTube Music. Learn investing on the go." />
        <meta name="keywords" content="Canadian investing podcast, finance podcast Canada, investing podcast free, Our New Normal podcast, Everyday Millionaires podcast, Deep Dive Investing Canada, financial podcast, wealth building podcast, money podcast Canada, Finance in Canada YouTube podcast, Harry Vadalkar podcast, TFSA podcast, RRSP podcast, ETF podcast Canada, stock market podcast Canada, Canadian wealth stories, career advice podcast, personal finance podcast Canada, investing audio content, YouTube Music finance" />
        <link rel="canonical" href="https://harryvadalkar.ca/podcasts" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1709846486154-f9172678be6f?w=1920&q=80"
            alt="Podcast Studio"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 to-[#020617]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-400 px-4 py-2 rounded-full mb-6">
            <Mic className="w-4 h-4" />
            <span className="font-medium">Audio Content</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            Financial <span className="text-purple-400">Podcasts</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Listen and learn on the go. Three unique podcasts covering money, careers, wealth stories, and Canadian investing.
          </p>
        </div>
      </section>

      {/* Podcasts Grid */}
      <section className="py-20" data-testid="podcasts-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {podcasts.map((podcast, index) => (
              <div
                key={index}
                className="glass rounded-2xl overflow-hidden hover:border-slate-700 transition-all duration-300 group"
              >
                <div className="relative aspect-square">
                  <img
                    src={podcast.image}
                    alt={podcast.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-2 ${
                      podcast.color === 'purple' ? 'bg-purple-500/30 text-purple-300' :
                      podcast.color === 'amber' ? 'bg-amber-500/30 text-amber-300' :
                      'bg-blue-500/30 text-blue-300'
                    }`}>
                      <Radio className="w-3 h-3" />
                      {podcast.episodes} Episodes
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{podcast.title}</h3>
                  <p className={`font-medium mb-3 ${
                    podcast.color === 'purple' ? 'text-purple-400' :
                    podcast.color === 'amber' ? 'text-amber-400' :
                    'text-blue-400'
                  }`}>
                    {podcast.subtitle}
                  </p>
                  <p className="text-slate-400 mb-6">{podcast.description}</p>
                  
                  <a
                    href={podcast.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`podcast-link-${index}`}
                    className={`inline-flex items-center justify-center w-full font-semibold px-6 py-4 rounded-xl transition-all ${
                      podcast.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                      podcast.color === 'amber' ? 'bg-amber-500 hover:bg-amber-600 text-black' :
                      'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <Headphones className="w-5 h-5 mr-2" />
                    Listen on YouTube Music
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube CTA */}
      <section className="py-20 bg-slate-900/30" data-testid="youtube-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Subscribe to <span className="text-red-500">Finance in Canada</span>
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Watch videos, tutorials, and more on our YouTube channel. Join 11,000+ subscribers.
          </p>
          <a
            href="https://www.youtube.com/@FinanceinCanada"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-subscribe"
            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-4 rounded-full transition-all shadow-lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Subscribe on YouTube
          </a>
        </div>
      </section>
    </div>
  );
};

export default Podcasts;